import React from 'react';
import PropTypes from 'prop-types';
import useSafeState from '@ubeya/shared/hooks/useSafeState';
import styled, { css } from 'styled-components';
import { ReactComponent as MoreIcon } from '../../assets/BookingBoard/menu.svg';
import BaseCircleIcon from '../CircleIcon';
import DropDown from '../DropDown';
import PromptClick from '../PromptClick/PromptClick';

const MENU_OFFSET = { top: 5, right: 10 };

const MoreIconWhite = styled(MoreIcon)`
  path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;

const CircleIcon = styled(BaseCircleIcon)`
  ${({ $whiteIcon }) =>
    $whiteIcon &&
    css`
      &:hover {
        background: rgba(255, 255, 255, 0.35);
      }
    `}
`;

const ContextMenu = ({
  className,
  children,
  menuOffset,
  usePortal,
  openFromRight,
  maxHeight,
  scrollingContainer,
  menuMinWidth,
  menuMaxWidth,
  useTextWithTooltip,
  options = [],
  hideIconBackground = false,
  controlledIsOpen = false,
  whiteIcon = false,
  isActive = false,
  showSearch = false,
  renderNextToSearchItems = null,
  isDisabled = false
}) => {
  const [promptOptions, setPromptOptions] = useSafeState();

  return (
    <>
      <DropDown
        showArrow={false}
        isDisabled={isDisabled}
        renderValue={
          children || (
            <CircleIcon
              icon={whiteIcon ? MoreIconWhite : MoreIcon}
              $whiteIcon={whiteIcon}
              hideBackground={hideIconBackground}
              isActive={isActive}
              isDisabled={isDisabled}
            />
          )
        }
        className={className}
        menuOffset={menuOffset ?? MENU_OFFSET}
        usePortal={usePortal}
        openFromRight={openFromRight}
        maxHeight={maxHeight}
        menuMinWidth={menuMinWidth}
        menuMaxWidth={menuMaxWidth}
        controlledIsOpen={controlledIsOpen}
        scrollingContainer={scrollingContainer}
        showSearch={showSearch}
        renderNextToSearchItems={renderNextToSearchItems}>
        {options
          .filter(({ visible }) => {
            return visible === undefined || visible;
          })
          .map((option, index) => {
            const optionProps = {};
            if (option.onClick) {
              optionProps.onClick = (e) => (option.confirm ? setPromptOptions(option) : option.onClick(e));
            }

            return (
              <DropDown.Item
                key={index}
                internalCss={option.css}
                icon={option.icon}
                isDisabled={option.disabled}
                useTextWithTooltip={useTextWithTooltip}
                closeOnClick={option.closeOnClick}
                customTooltip={option.customTooltip}
                rawSearchLabel={option.rawSearchLabel}
                {...optionProps}>
                {option.label}
              </DropDown.Item>
            );
          })}
      </DropDown>

      {promptOptions && (
        <PromptClick
          title={promptOptions.confirmTitle}
          body={promptOptions.confirmBody}
          confirmationModalContainerStyle={promptOptions.confirmationModalContainerStyle}
          renderBody={promptOptions.confirmRenderBody}
          isOpen
          onClose={() => setPromptOptions()}
          onClick={promptOptions.onClick}
          cancelText={promptOptions.cancelText}
          confirmText={promptOptions.confirmText}
          showCancelButton={promptOptions.confirmCancelButton}
          usePortal={promptOptions.confirmPortal}
          isWaitingForResponse={promptOptions.confirmIsWaitingForResponse}
          errors={promptOptions.errors}
          disabledConfirmTitle={promptOptions.disabledConfirmTitle}
          isPermanentAction={promptOptions.isPermanentAction}
        />
      )}
    </>
  );
};

ContextMenu.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      onClick: PropTypes.func,
      confirm: PropTypes.bool,
      confirmBody: PropTypes.string,
      confirmText: PropTypes.string,
      confirmPortal: PropTypes.bool
    })
  )
};

export default ContextMenu;
